import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Badge, BadgeProps, Tab, TabProps, useTheme } from '@mui/material';
import React from 'react';

interface ThemedTabProps extends Omit<TabProps, 'label'> {
  label: string;
  withBadge?: boolean;
  badgeContent?: BadgeProps['badgeContent'];
}

export const ThemedTab: React.FC<ThemedTabProps> = ({
  label,
  withBadge = false,
  badgeContent,
  ...tabProps
}) => {
  const { tokens } = useTheme<LensTheme>();

  const tabStyles = {
    backgroundColor: 'transparent',
    position: 'relative',
    textTransform: 'none',
    borderRadius: `${tokens.semantic.radius.lg.value} ${tokens.semantic.radius.lg.value} 0 0`,
    color: tokens.semantic.color.action.secondary.onSecondary.value,
    padding: `${tokens.core.spacing[1].value} ${tokens.core.spacing['2_5'].value}`,
    width: 'fit-content',
    height: 'auto',
    '&.Mui-selected::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '2px',
      zIndex: 1,
    },
    '&:not(.Mui-selected)::after': {
      display: 'none',
    },
    '& .MuiTab-wrapper, & .MuiTab-labelContainer, &': {
      fontFamily: tokens.semantic.font.text.body.fontFamily.value,
      fontSize: `${tokens.semantic.font.text.body.fontSize.value}`,
      fontWeight: tokens.semantic.fontWeight.emphasis.value,
      letterSpacing: `${tokens.semantic.font.text.body.letterSpacing.value}`,
      lineHeight: `${tokens.semantic.font.text.body.lineHeight.value}`,
    },
    '&.Mui-selected': {
      color: tokens.semantic.color.type.default.value,
    },
    '&:hover': {
      backgroundColor: tokens.semantic.color.action.secondary.hoverFill.value,
    },
    '&:active': {
      backgroundColor: tokens.semantic.color.action.secondary.activeFill.value,
    },
    '& .MuiBadge-root': {
      gap: tokens.core.spacing['1_5'].value,
    },
  };

  const tabLabel = withBadge ? (
    <Badge badgeContent={badgeContent} color="error">
      {label}
    </Badge>
  ) : (
    label
  );

  return <Tab {...tabProps} sx={tabStyles} label={tabLabel} />;
};
